import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Send, ShieldCheck, MessageCircle } from "lucide-react";
import { PHONE } from "@/lib/api";

const initial = {
  name: "",
  phone: "",
  pickup: "",
  drop: "",
  travel_date: "",
  vehicle_type: "Sedan",
  notes: "",
};

const ADVANCE_AMOUNT = 1000;

const openWhatsAppWith = (form) => {
  const lines = [
    "*New Booking — Amyee Travels (Advance PAID ₹1000)*",
    "",
    `*Name:* ${form.name}`,
    `*Phone:* ${form.phone}`,
    `*Pickup:* ${form.pickup}`,
    form.drop ? `*Drop:* ${form.drop}` : null,
    `*Travel date:* ${form.travel_date}`,
    `*Vehicle:* ${form.vehicle_type}`,
    form.notes ? `*Notes:* ${form.notes}` : null,
  ].filter(Boolean);
  const waUrl = `https://wa.me/91${PHONE}?text=${encodeURIComponent(lines.join("\n"))}`;
  window.open(waUrl, "_blank", "noopener,noreferrer");
};

const openWhatsAppWithoutPayment = (form) => {
  const lines = [
    "*New Booking Request — Amyee Travels*",
    "",
    `*Name:* ${form.name}`,
    `*Phone:* ${form.phone}`,
    `*Pickup:* ${form.pickup}`,
    form.drop ? `*Drop:* ${form.drop}` : null,
    `*Travel date:* ${form.travel_date}`,
    `*Vehicle:* ${form.vehicle_type}`,
    form.notes ? `*Notes:* ${form.notes}` : null,
  ].filter(Boolean);
  return `https://wa.me/91${PHONE}?text=${encodeURIComponent(lines.join("\n"))}`;
};

export const BookingForm = () => {
  const [form, setForm] = useState(initial);
  const [payLoading, setPayLoading] = useState(false);
  const [waLoading, setWaLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.name.trim() || !form.phone.trim() || !form.pickup.trim() || !form.travel_date) {
      toast.error("Please fill all required fields.");
      return false;
    }
    if (!/^[0-9]{10}$/.test(form.phone.trim())) {
      toast.error("Enter a valid 10-digit phone number.");
      return false;
    }
    return true;
  };

  // Flow 1: Send booking on WhatsApp (no payment)
  const submitWhatsApp = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setWaLoading(true);
    const waWindow = window.open("", "_blank", "noopener,noreferrer");
    try {
      await api.post("/bookings", form);
      toast.success("Opening WhatsApp to send your booking…");
      const waUrl = openWhatsAppWithoutPayment(form);
      if (waWindow) waWindow.location.href = waUrl;
      else window.location.href = waUrl;
      setForm(initial);
    } catch (err) {
      console.error(err);
      if (waWindow) waWindow.close();
      toast.error("Could not submit. Please call 6372045675.");
    } finally {
      setWaLoading(false);
    }
  };

  // Flow 2: Pay ₹1000 advance and confirm booking
  const payAndBook = async () => {
    if (!validate()) return;
    if (!window.Razorpay) {
      toast.error("Payment gateway not loaded. Please refresh the page.");
      return;
    }
    setPayLoading(true);
    try {
      const orderRes = await api.post("/payment/create-order", {
        name: form.name,
        phone: form.phone,
      });
      const { order_id, amount, currency, key_id } = orderRes.data;

      const options = {
        key: key_id,
        amount,
        currency,
        name: "Amyee Travels",
        description: `Advance ₹${ADVANCE_AMOUNT} for ${form.vehicle_type} booking`,
        order_id,
        prefill: {
          name: form.name,
          contact: form.phone,
        },
        theme: { color: "#D95D39" },
        handler: async (response) => {
          try {
            await api.post("/payment/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              booking: form,
            });
            toast.success("Payment confirmed! Booking is locked in.");
            openWhatsAppWith(form);
            setForm(initial);
          } catch (err) {
            console.error(err);
            toast.error("Payment received but booking save failed. Call us: 6372045675");
          } finally {
            setPayLoading(false);
          }
        },
        modal: {
          ondismiss: () => {
            setPayLoading(false);
            toast.message("Payment cancelled. You can try again.");
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        console.error(resp);
        toast.error("Payment failed. Please try again.");
        setPayLoading(false);
      });
      rzp.open();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Could not start payment. Try again.");
      setPayLoading(false);
    }
  };

  const inputCls =
    "bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 h-12 focus-visible:border-[#D4AF37] focus-visible:ring-0 focus-visible:ring-offset-0 text-white placeholder:text-gray-600 text-base";

  return (
    <form
      onSubmit={submitWhatsApp}
      data-testid="booking-form"
      className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8"
    >
      <div>
        <Label className="text-[10px] uppercase tracking-[0.25em] text-gray-400 mb-1 block">
          Full name
        </Label>
        <Input
          data-testid="booking-name-input"
          value={form.name}
          onChange={(e) => update("name", e.target.value)}
          placeholder="Your name"
          className={inputCls}
        />
      </div>

      <div>
        <Label className="text-[10px] uppercase tracking-[0.25em] text-gray-400 mb-1 block">
          Phone number
        </Label>
        <Input
          data-testid="booking-phone-input"
          value={form.phone}
          onChange={(e) => update("phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
          placeholder="10-digit mobile"
          inputMode="numeric"
          className={inputCls}
        />
      </div>

      <div>
        <Label className="text-[10px] uppercase tracking-[0.25em] text-gray-400 mb-1 block">
          Pickup location
        </Label>
        <Input
          data-testid="booking-pickup-input"
          value={form.pickup}
          onChange={(e) => update("pickup", e.target.value)}
          placeholder="e.g. Harichandanpur"
          className={inputCls}
        />
      </div>

      <div>
        <Label className="text-[10px] uppercase tracking-[0.25em] text-gray-400 mb-1 block">
          Drop location <span className="text-gray-600 normal-case tracking-normal">(optional)</span>
        </Label>
        <Input
          data-testid="booking-drop-input"
          value={form.drop}
          onChange={(e) => update("drop", e.target.value)}
          placeholder="e.g. Ghatagaon Temple"
          className={inputCls}
        />
      </div>

      <div>
        <Label className="text-[10px] uppercase tracking-[0.25em] text-gray-400 mb-1 block">
          Travel date
        </Label>
        <Input
          data-testid="booking-date-input"
          type="date"
          value={form.travel_date}
          onChange={(e) => update("travel_date", e.target.value)}
          className={`${inputCls} [color-scheme:dark]`}
        />
      </div>

      <div>
        <Label className="text-[10px] uppercase tracking-[0.25em] text-gray-400 mb-1 block">
          Vehicle type
        </Label>
        <Select value={form.vehicle_type} onValueChange={(v) => update("vehicle_type", v)}>
          <SelectTrigger
            data-testid="booking-vehicle-select"
            className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 h-12 text-white focus:ring-0 focus:ring-offset-0 [&>span]:text-left"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#131B2A] border-white/10 text-white">
            <SelectItem value="Sedan">Sedan (Dzire / Etios)</SelectItem>
            <SelectItem value="SUV">SUV (Ertiga / Innova)</SelectItem>
            <SelectItem value="Premium">Premium (Innova Crysta)</SelectItem>
            <SelectItem value="Tempo">Tempo Traveller</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="md:col-span-2">
        <Label className="text-[10px] uppercase tracking-[0.25em] text-gray-400 mb-1 block">
          Notes (optional)
        </Label>
        <Textarea
          data-testid="booking-notes-input"
          value={form.notes}
          onChange={(e) => update("notes", e.target.value)}
          placeholder="Any special requests, stops, pickup time…"
          rows={3}
          className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 focus-visible:border-[#D4AF37] focus-visible:ring-0 focus-visible:ring-offset-0 text-white placeholder:text-gray-600 resize-none"
        />
      </div>

      <div className="md:col-span-2 pt-4 flex flex-col sm:flex-row gap-4">
        <Button
          type="button"
          onClick={payAndBook}
          data-testid="booking-pay-advance-button"
          disabled={payLoading || waLoading}
          className="bg-[#D95D39] hover:bg-[#E27D60] text-white rounded-none px-8 py-6 h-auto text-base tracking-wider uppercase flex items-center gap-3 disabled:opacity-60"
        >
          {payLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          {payLoading ? "Opening…" : `Pay ₹${ADVANCE_AMOUNT} Advance & Confirm`}
        </Button>

        <Button
          type="submit"
          data-testid="booking-submit-button"
          disabled={payLoading || waLoading}
          variant="outline"
          className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0E17] bg-transparent rounded-none px-8 py-6 h-auto text-base tracking-wider uppercase flex items-center gap-3 disabled:opacity-60"
        >
          {waLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
          {waLoading ? "Sending…" : "Or Just Send on WhatsApp"}
        </Button>
      </div>

      <p className="md:col-span-2 text-xs text-gray-500 -mt-2">
        Pay ₹{ADVANCE_AMOUNT} advance to lock your booking instantly. Balance payable to the driver.
        Advance is adjusted against the final fare. Refundable up to 4 hours before travel. Terms &amp; conditions apply.
      </p>
    </form>
  );
};
